/* sb-classroom — curriculum browser.
   Ported from the output/classroom-page prototype, 2026-09-17.

   Three things differ from the prototype on purpose:
   - Lesson data comes from assets/sb-classroom-lessons.json, fetched once, rather
     than an inline data.js. Liquid passes the versioned asset_url on the root, so
     the CDN cannot serve a stale copy the way a bare /assets/ path would.
   - The prototype linked each lesson to a page inside a local curriculum PDF.
     There is still no per-LESSON link: that needs a page anchor the split unit
     files do not carry. Per-UNIT view and download links landed 2026-09-21;
     since 2026-09-28 both are pill buttons directly under the unit description,
     above the lessons.
   - The browser is EDITION AWARE. The one region picker in the curriculum band
     emits `sb-classroom:region` on the section root; the `edition` on that event
     selects an overlay from `editions` in the JSON. Editions are Canada, NGSS
     and TEKS — NOT countries, because Texas runs TEKS while the other 50 US
     regions run NGSS. TEKS is a 41-lesson edition whose Unit 5 stops at lesson
     1, which `lessonLimits` expresses. The Canadian edition ships a
     single grades 4-6 volume, so its overlay sets `gradeSplit: false` and the
     Grade 4 / Grade 5 toggle is replaced by a band note — the same nine units
     are simply all listed at once. Do not hardcode a country here; add an
     overlay to the JSON instead.

   Since 2026-09-28 it also owns the summary row above the downloads: the
   edition pill (`pill` in each overlay), the lesson count, and
   `data-active-grade` on the root, which the CSS reads to put the selected
   grade's book first in the downloads panel. The optional "included with every
   unit" list comes from a <template data-unit-included> in the section. */
(function () {
  const root = document.querySelector('[data-sb-classroom]');
  if (!root) return;

  let images = {};
  try { images = JSON.parse(root.dataset.images || '{}'); } catch (e) { images = {}; }

  /* Keyed "<edition>|<unit>", e.g. "Canada|4". An edition with no entries simply
     renders no unit links, which is how this degrades. */
  let unitFiles = {};
  try { unitFiles = JSON.parse(root.dataset.unitFiles || '{}'); } catch (e) { unitFiles = {}; }

  const viewLabel = root.dataset.unitViewLabel || 'View unit';
  const dlLabel = root.dataset.unitDownloadLabel || 'Download unit';

  /* ---- contact popup -----------------------------------------------------
     The closing button links to a store page. When the section rendered that
     page into <dialog data-contact-modal>, open the dialog instead of following
     the link. With no dialog, or no showModal, the link's own target="_blank"
     still keeps the teacher on this page. */
  const contactModal = root.querySelector('[data-contact-modal]');
  const contactOpen = root.querySelector('[data-contact-open]');
  if (contactModal && contactOpen && contactModal.showModal) {
    contactOpen.addEventListener('click', function (ev) {
      ev.preventDefault();
      contactModal.showModal();
    });
    const contactX = contactModal.querySelector('[data-contact-close]');
    if (contactX) contactX.addEventListener('click', function () { contactModal.close(); });
    /* A click on the backdrop lands on the dialog element itself. */
    contactModal.addEventListener('click', function (ev) {
      if (ev.target === contactModal) contactModal.close();
    });
  }

  /* ---- curriculum browser ------------------------------------------------ */
  const nav = root.querySelector('[data-unit-nav]');
  const panel = root.querySelector('[data-unit-panel]');
  if (!nav || !panel) return;

  const toggle = root.querySelector('[data-grade-toggle]');
  const bandNote = root.querySelector('[data-band-note]');

  /* The inline gate script may already have chosen an edition (market
     preselect) before this deferred file ran, so start from what it wrote. */
  let units = [], lessons = {}, editions = {}, grade = 4, selected = 1,
    edition = root.dataset.edition || '';

  const pill = root.querySelector('[data-cr-pill]');
  const count = root.querySelector('[data-cr-count]');
  const countNum = root.querySelector('[data-cr-count-num]');
  const countLabel = root.querySelector('[data-cr-count-label]');
  const gradeLabel = root.dataset.countGradeLabel || 'lessons in Grade [grade]';
  const includedTpl = root.querySelector('[data-unit-included]');
  const included = includedTpl ? includedTpl.innerHTML : '';

  const esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  };

  /* No region chosen, or an edition with no overlay, falls back to the default
     grade-split view. That is deliberately the NGSS shape, because the base
     `units` / `lessons` data is written in it — NGSS therefore needs no
     overrides beyond declaring gradeSplit. */
  function cfg() { return editions[edition] || { gradeSplit: true }; }

  function unitTitle(u) {
    const o = cfg().unitTitles;
    return (o && o[String(u.id)]) || u.title;
  }

  function lessonTitle(u, i) {
    const o = cfg().lessonTitles;
    const forUnit = o && o[String(u.id)];
    return (forUnit && forUnit[String(i + 1)]) || u.lessons[i];
  }

  /* TEKS ships fewer lessons in Unit 5 than NGSS and Canada do. The base data is
     the NGSS shape, so an edition trims rather than adds. */
  function lessonsFor(u) {
    const lim = cfg().lessonLimits;
    const n = lim && lim[String(u.id)];
    return n ? u.lessons.slice(0, n) : u.lessons;
  }

  /* Keyed off the UNIT's own grade, not the current toggle: when gradeSplit is
     false every unit renders at once and a toggle-based index picks the wrong
     descriptions for units 5-9. */
  function unitInfo(u) {
    const set = lessons[String(u.grade)] || [];
    return set[u.id - (u.grade === 4 ? 1 : 5)] || [];
  }

  function fileFor(u) {
    return edition ? unitFiles[edition + '|' + u.id] : '';
  }

  function nameFor(url, u) {
    try {
      const tail = decodeURIComponent(url.split('?')[0].split('/').pop());
      if (tail) return tail;
    } catch (e) { /* fall through to the generated name */ }
    return 'SparkBlocks-Unit-' + u.id + '.pdf';
  }

  /* ---- unit preview modal ------------------------------------------------
     "View unit" opens the PDF in a dialog rather than handing it to the browser,
     which for many people is configured to download a .pdf rather than render it.

     Two cases deliberately fall back to a new tab instead:
     - iOS/iPadOS Safari renders only the FIRST page of a PDF in an iframe. There
       is no workaround short of shipping PDF.js, so those devices get the tab.
     - Narrow screens, where a framed PDF is unreadable anyway.
     The dialog is also skipped entirely if <dialog> has no showModal. */
  const modal = root.querySelector('[data-unit-modal]');
  const frame = root.querySelector('[data-unit-modal-frame]');
  const modalTitle = root.querySelector('[data-unit-modal-title]');
  const modalTab = root.querySelector('[data-unit-modal-tab]');

  const isIOS = /iP(hone|ad|od)/.test(navigator.platform) ||
    (navigator.userAgent.indexOf('Mac') > -1 && 'ontouchend' in document);

  function canPreview() {
    return !!(modal && frame && modal.showModal) &&
      !isIOS &&
      window.matchMedia('(min-width: 750px)').matches;
  }

  /* Clearing src matters: an open iframe keeps a multi-megabyte PDF resident,
     and Unit 4 of the NGSS set is over 2 MB. It is done BOTH here and on the
     `close` event on purpose. The event alone is correct per spec and is the
     only hook for a UA-driven Escape, but it could not be observed in the
     verification harness, so the paths we control clear the frame directly
     rather than trusting it. */
  function clearFrame() {
    if (frame && frame.src !== 'about:blank') frame.src = 'about:blank';
  }

  function closePreview() {
    if (!modal || !modal.open) return;
    modal.close();
    clearFrame();
  }

  function openPreview(url, label) {
    if (modalTitle) modalTitle.textContent = label || '';
    if (modalTab) modalTab.href = url;
    frame.src = url;
    modal.showModal();
  }

  if (modal) {
    /* Covers Escape and anything else that closes the dialog without going
       through closePreview. */
    modal.addEventListener('close', clearFrame);
    modal.addEventListener('cancel', clearFrame);
    const x = root.querySelector('[data-unit-modal-close]');
    if (x) x.addEventListener('click', closePreview);
    /* Click on the backdrop, i.e. on the dialog element itself rather than any
       of its children, closes. */
    modal.addEventListener('click', function (ev) {
      if (ev.target === modal) closePreview();
    });
  }

  /* Shopify serves these PDFs with no content-disposition and the storefront is
     a different origin from cdn.shopify.com, so the `download` attribute alone
     is ignored and both links would just open the file. The CDN does send
     `access-control-allow-origin: *`, so fetching a blob gives a real Save. If
     that fetch fails for any reason, fall back to opening the URL. */
  function saveFile(url, name, btn) {
    const done = function () { btn.removeAttribute('aria-busy'); };
    btn.setAttribute('aria-busy', 'true');
    fetch(url)
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.blob(); })
      .then(function (blob) {
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(function () { URL.revokeObjectURL(href); }, 30000);
        done();
      })
      .catch(function () { done(); window.open(url, '_blank', 'noopener'); });
  }

  function renderPanel() {
    const u = units.find(function (x) { return x.id === selected; });
    if (!u) return;
    const info = unitInfo(u);
    const shown = lessonsFor(u);
    const rows = shown.map(function (title, i) {
      const d = info[i] || {};
      let body = '';
      if (d.big) body += '<p class="lesson-big">' + esc(d.big) + '</p>';
      if (d.desc) body += '<p class="lesson-desc">' + esc(d.desc) + '</p>';
      if (d.eq) body += '<p class="lesson-eq"><span>Essential question</span>' + esc(d.eq) + '</p>';
      if (!body) body = '<p class="lesson-none">A summary for this lesson is not in the supplied curriculum file.</p>';
      return '<li><details><summary><span class="lesson-badge">Lesson ' + (i + 1) + '</span>' +
        '<span class="lesson-title">' + esc(lessonTitle(u, i)) + '</span></summary>' +
        '<div class="lesson-body">' + body + '</div></details></li>';
    }).join('');

    /* Heading copy follows Michelle's 2026-09-28 mockup: "Grade 4 · Unit 1 ·
       6 lessons" over the unit TITLE, with the driving question kept as a line
       under it. A single-book edition (Canada) has no grade to name. */
    const count = shown.length + ' lesson' + (shown.length === 1 ? '' : 's');
    const meta = (cfg().gradeSplit !== false ? 'Grade ' + u.grade + ' &middot; ' : '') +
      'Unit ' + u.id + ' &middot; ' + count;

    const url = fileFor(u);
    const links = url
      ? '<p class="unit-files">' +
        '<a class="button small" href="' + esc(url) + '" data-unit-download>' + esc(dlLabel) + '</a>' +
        '<a class="button small button--outline" href="' + esc(url) + '" target="_blank" rel="noopener" data-unit-view>' + esc(viewLabel) + '</a>' +
        '</p>'
      : '';

    panel.innerHTML =
      '<div class="unit-top"><div><small>' + meta + '</small><h3>' + esc(unitTitle(u)) + '</h3>' +
      (u.question ? '<p class="unit-question">' + esc(u.question) + '</p>' : '') + '</div>' +
      '<img src="' + esc(images[u.image] || '') + '" alt="" loading="lazy" width="200" height="160"></div>' +
      '<p class="unit-learn">' + esc(u.learn) + '</p>' + links +
      '<ol class="lesson-accordion">' + rows + '</ol>' + included;

    const dl = panel.querySelector('[data-unit-download]');
    if (dl) {
      dl.addEventListener('click', function (ev) {
        ev.preventDefault();
        saveFile(url, nameFor(url, u), dl);
      });
    }

    const view = panel.querySelector('[data-unit-view]');
    if (view) {
      view.addEventListener('click', function (ev) {
        /* No preventDefault when we cannot preview: the anchor's own
           target="_blank" is the fallback, so it keeps working with JS half
           loaded or on iOS. */
        if (!canPreview()) return;
        ev.preventDefault();
        openPreview(url, 'Unit ' + u.id + ' · ' + unitTitle(u));
      });
    }

    nav.querySelectorAll('[data-unit]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(Number(b.dataset.unit) === selected));
    });
  }

  /* No edition yet (no country or region chosen) means no pill: the default
     view is the NGSS shape, but claiming a framework before the teacher has
     said where they are would be a guess. */
  function renderSummary(split, available) {
    const c = cfg();
    if (pill) {
      pill.textContent = c.pill || '';
      pill.dataset.edition = edition;
      pill.hidden = !c.pill;
    }
    const total = available.reduce(function (n, u) { return n + lessonsFor(u).length; }, 0);
    if (countNum) countNum.textContent = String(total);
    if (countLabel) {
      countLabel.textContent = split
        ? gradeLabel.replace('[grade]', String(grade))
        : (c.countLabel || '');
    }
    if (count) count.hidden = !total;
    root.dataset.activeGrade = split ? String(grade) : 'all';
  }

  function renderNav() {
    const split = cfg().gradeSplit !== false;
    const available = split
      ? units.filter(function (u) { return u.grade === grade; })
      : units.slice();
    if (!available.length) return;

    if (toggle) toggle.hidden = !split;
    if (bandNote) {
      const note = split ? '' : (cfg().bandLabel || '');
      bandNote.textContent = note;
      bandNote.hidden = !note;
    }

    renderSummary(split, available);

    if (!available.some(function (u) { return u.id === selected; })) selected = available[0].id;
    nav.innerHTML = available.map(function (u) {
      return '<button type="button" data-unit="' + u.id + '" aria-pressed="' + (u.id === selected) + '">' +
        '<strong>Unit ' + u.id + '</strong><span>' + esc(unitTitle(u)) + '</span></button>';
    }).join('');
    nav.querySelectorAll('[data-unit]').forEach(function (b) {
      b.addEventListener('click', function () { selected = Number(b.dataset.unit); renderPanel(); });
    });
    renderPanel();
  }

  root.querySelectorAll('[data-grade]').forEach(function (b) {
    b.addEventListener('click', function () {
      grade = Number(b.dataset.grade);
      root.querySelectorAll('[data-grade]').forEach(function (x) {
        x.setAttribute('aria-pressed', String(x === b));
      });
      renderNav();
    });
  });

  /* Emitted by the inline gate script in sb-classroom.liquid. */
  root.addEventListener('sb-classroom:region', function (ev) {
    edition = (ev.detail && ev.detail.edition) || '';
    if (units.length) renderNav();
  });

  fetch(root.dataset.lessons)
    .then(function (r) {
      if (!r.ok) throw new Error('lesson data ' + r.status);
      return r.json();
    })
    .then(function (data) {
      units = data.units || [];
      lessons = data.lessons || {};
      editions = data.editions || {};
      if (units.length) renderNav();
    })
    .catch(function (err) {
      // The browser is progressive enhancement: leave the fallback copy in place.
      console.error('[sb-classroom]', err);
      panel.innerHTML = '<p class="unit-learn">The curriculum browser could not load. ' +
        'Use the download panel below to open the full curriculum.</p>';
    });
})();
